import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import {
  Calendar, MapPin, Loader2, Leaf, Sun, Snowflake, Sprout, Flower2
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const PlantingCalendarPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [location, setLocation] = useState({ lat: null, lon: null });
  const [calendar, setCalendar] = useState(null);
  const [filterSeason, setFilterSeason] = useState("");
  const [filterCategory, setFilterCategory] = useState("");

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setLocation({
            lat: pos.coords.latitude,
            lon: pos.coords.longitude
          });
        },
        () => {
          setLocation({ lat: 40.7128, lon: -74.0060 });
        }
      );
    }
  }, []);

  useEffect(() => {
    if (location.lat && location.lon) {
      fetchCalendar();
    }
  }, [location]);

  const fetchCalendar = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/planting-calendar?lat=${location.lat}&lon=${location.lon}`
      );
      setCalendar(response.data);
    } catch (error) {
      toast.error("Failed to generate planting calendar");
    } finally {
      setLoading(false);
    }
  };

  const filteredCalendar = calendar?.calendar?.filter(entry => {
    if (filterSeason && entry.growing_season !== filterSeason) return false;
    if (filterCategory && entry.category !== filterCategory) return false;
    return true;
  }) || [];

  const getSeasonIcon = (season) => {
    switch (season) {
      case "cool": return <Snowflake className="w-4 h-4 text-blue-500" />;
      case "warm": return <Sun className="w-4 h-4 text-orange-500" />;
      default: return <Flower2 className="w-4 h-4 text-purple-500" />;
    }
  };

  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-amber-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur border-b border-border sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate("/dashboard")}>
              ← Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-foreground flex items-center gap-2">
                <Calendar className="w-6 h-6 text-green-500" />
                Planting Calendar
              </h1>
              {calendar && (
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  Zone {calendar.estimated_zone} | Last Frost: {calendar.last_frost_date}
                </p>
              )}
            </div>
          </div>
          <Button variant="outline" onClick={fetchCalendar} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Refresh"}
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {loading && !calendar ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : calendar ? (
          <div className="space-y-6">
            {/* Zone Info */}
            <div className="grid md:grid-cols-3 gap-4">
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-4 flex items-center gap-3">
                  <Snowflake className="w-8 h-8 text-blue-500" />
                  <div>
                    <p className="text-sm text-blue-600">Last Frost Date</p>
                    <p className="font-bold text-blue-900">{calendar.last_frost_date}</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-orange-50 border-orange-200">
                <CardContent className="p-4 flex items-center gap-3">
                  <Snowflake className="w-8 h-8 text-orange-500" />
                  <div>
                    <p className="text-sm text-orange-600">First Frost Date</p>
                    <p className="font-bold text-orange-900">{calendar.first_frost_date}</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-4 flex items-center gap-3">
                  <Leaf className="w-8 h-8 text-green-500" />
                  <div>
                    <p className="text-sm text-green-600">Hardiness Zone</p>
                    <p className="font-bold text-green-900">{calendar.estimated_zone}</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-4">
              <select
                value={filterSeason}
                onChange={(e) => setFilterSeason(e.target.value)}
                className="rounded-md border p-2"
              >
                <option value="">All Seasons</option>
                <option value="cool">Cool Season</option>
                <option value="warm">Warm Season</option>
                <option value="both">Both Seasons</option>
              </select>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="rounded-md border p-2"
              >
                <option value="">All Categories</option>
                <option value="vegetable">Vegetables</option>
                <option value="herb">Herbs</option>
                <option value="fruit">Fruits</option>
                <option value="flower">Flowers</option>
              </select>
            </div>

            {/* Calendar Timeline */}
            <Card>
              <CardHeader>
                <CardTitle>Monthly Planting Guide</CardTitle>
              </CardHeader>
              <CardContent>
                {/* Month Headers */}
                <div className="grid grid-cols-13 gap-1 mb-4 sticky top-0 bg-white z-10">
                  <div className="font-medium text-sm p-2">Plant</div>
                  {months.map(month => (
                    <div key={month} className="text-center text-xs font-medium p-1 bg-muted rounded">
                      {month}
                    </div>
                  ))}
                </div>

                {/* Plant Rows */}
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {filteredCalendar.map((entry, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.02 }}
                      className="grid grid-cols-13 gap-1 items-center"
                    >
                      <div className="flex items-center gap-1 text-sm font-medium truncate p-2">
                        {getSeasonIcon(entry.growing_season)}
                        <span className="truncate">{entry.plant_name}</span>
                      </div>
                      {months.map((month, mIdx) => {
                        // Simple logic to show activity based on season
                        const isCool = entry.growing_season === "cool" || entry.frost_tolerant;
                        const isWarm = entry.growing_season === "warm" || entry.growing_season === "both";
                        
                        let activity = null;
                        
                        // Sow indoors (Feb-Mar for cool, Mar-Apr for warm)
                        if ((isCool && (mIdx === 1 || mIdx === 2)) || (isWarm && (mIdx === 2 || mIdx === 3))) {
                          activity = "sow";
                        }
                        // Transplant (Mar-Apr for cool, May-Jun for warm)
                        else if ((isCool && (mIdx === 2 || mIdx === 3)) || (isWarm && (mIdx === 4 || mIdx === 5))) {
                          activity = "transplant";
                        }
                        // Direct sow (Apr-May for cool, May-Jun for warm)
                        else if ((isCool && (mIdx === 3 || mIdx === 4)) || (isWarm && (mIdx === 4 || mIdx === 5))) {
                          activity = "direct";
                        }
                        // Harvest (based on days to harvest, rough estimate)
                        else if (entry.days_to_harvest) {
                          const harvestStart = isWarm ? 6 : 4;
                          const harvestMonths = Math.ceil(entry.days_to_harvest / 30);
                          if (mIdx >= harvestStart && mIdx < harvestStart + Math.min(3, harvestMonths)) {
                            activity = "harvest";
                          }
                        }

                        const colors = {
                          sow: "bg-yellow-200 border-yellow-400",
                          transplant: "bg-green-200 border-green-400",
                          direct: "bg-blue-200 border-blue-400",
                          harvest: "bg-orange-200 border-orange-400"
                        };

                        return (
                          <div
                            key={month}
                            className={`h-6 rounded border ${activity ? colors[activity] : 'bg-gray-50 border-gray-100'}`}
                            title={activity ? `${month}: ${activity}` : ''}
                          />
                        );
                      })}
                    </motion.div>
                  ))}
                </div>

                {/* Legend */}
                <div className="mt-6 flex flex-wrap gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-yellow-200 border border-yellow-400" />
                    <span>Sow Indoors</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-green-200 border border-green-400" />
                    <span>Transplant</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-blue-200 border border-blue-400" />
                    <span>Direct Sow</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-orange-200 border border-orange-400" />
                    <span>Harvest</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="text-center py-20">
            <Calendar className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">Loading your personalized planting calendar...</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default PlantingCalendarPage;
